import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronRight, Clock3, Play, Sparkles } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../services/api";
import usePullToRefresh from "../hooks/usePullToRefresh";

function StarlineBetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [markets, setMarkets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [results, setResults] = useState([]);
  const [marketId, setMarketId] = useState(id || "");
  const loadStarlineData = useCallback(async () => {
    const [controlRes, resultRes] = await Promise.all([
      API.get("/starline/control"),
      API.get("/starline/results").catch(() => ({ data: [] })),
    ]);
    const marketRows = Array.isArray(controlRes.data?.markets) ? controlRes.data.markets : [];
    const slotRows = Array.isArray(controlRes.data?.slots)
      ? controlRes.data.slots.filter((slot) => Number(slot.is_active) === 1)
      : [];
    const selectedMarket = String(id || marketRows[0]?.id || "");

    setMarkets(marketRows);
    setSlots(slotRows);
    setResults(Array.isArray(resultRes.data) ? resultRes.data : []);
    setMarketId(selectedMarket);
  }, [id]);
  const pullToRefresh = usePullToRefresh(loadStarlineData);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    let mounted = true;
    loadStarlineData()
      .then(() => {
        if (!mounted) return;
      })
      .catch(() => {
        if (!mounted) return;
        setMarkets([]);
        setSlots([]);
        setResults([]);
      });
    return () => {
      mounted = false;
    };
  }, [loadStarlineData]);

  const marketSlots = slots.filter((slot) => String(slot.market_id) === String(marketId));
  const market = markets.find((item) => String(item.id) === String(marketId)) || markets[0];

  const openSlot = (slot) => {
    navigate(`/starline/${marketId}/play?slot=${encodeURIComponent(slot.time_slot)}`);
  };

  return (
    <div style={page} {...pullToRefresh.bind}>
      <header style={header}>
        <button type="button" style={backButton} onClick={() => navigate("/dashboard", { replace: true })}>
          <ArrowLeft size={26} />
        </button>
        <h1 style={title}>
          <Sparkles size={20} color="#f59e0b" fill="#f59e0b" />
          {market?.name || "Starline"}
        </h1>
        <span style={headerSpacer} />
      </header>

      <main style={main}>
        <button type="button" style={historyCard} onClick={() => navigate("/bids?type=starline")}>
          <span style={historyIcon}><Clock3 size={28} /></span>
          <span style={historyText}>
            <b>Starline Bid History</b>
            <small>Tap to view all Starline bids</small>
          </span>
          <span style={historyArrow}><ChevronRight size={20} /></span>
        </button>

        <section style={slotList}>
          {marketSlots.map((slot) => {
            const result = resultForSlot(results, marketId, slot.time_slot);
            const playable = isSlotPlayable(slot.time_slot);
            return (
              <article key={slot.id} style={slotMarketCard}>
                <span style={playable ? timePillActive : timePill}>{slot.time_slot}</span>
                <strong style={result ? slotResult : slotResultPending}>{result ? `${result.pana}-${result.digit}` : "***-*"}</strong>
                <button type="button" style={playable ? slotPlayButton : slotPlayDisabled} disabled={!playable} onClick={() => openSlot(slot)}>
                  <Play size={16} fill="currentColor" /> Play
                </button>
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}

const toLocalDateKey = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return String(value).slice(0, 10);
};
const todayKey = () => toLocalDateKey(new Date());
const resultForSlot = (results, marketId, timeSlot) => {
  const matchesSlot = (item) =>
    String(item.market_id) === String(marketId) &&
    String(item.result_hour) === String(timeSlot);

  const todaysResult = results.find((item) => matchesSlot(item) && toLocalDateKey(item.result_date) === todayKey());
  if (todaysResult) return todaysResult;
  return results.find((item) => matchesSlot(item)) || null;
};
const timeToMinutes = (label = "") => {
  const match = String(label).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridian = match[3].toUpperCase();
  if (meridian === "PM" && hour !== 12) hour += 12;
  if (meridian === "AM" && hour === 12) hour = 0;
  return hour * 60 + minute;
};
const isSlotPlayable = (timeSlot) => {
  const now = new Date();
  if (now.getHours() === 1) return true;
  return timeToMinutes(timeSlot) >= now.getHours() * 60 + now.getMinutes();
};

const page = { minHeight: "100vh", background: "linear-gradient(180deg,#ffffff 0%, #f8fafc 100%)", paddingBottom: 24, fontFamily: "Inter, system-ui, sans-serif", color: "#111827" };
const header = { height: 58, background: "rgba(255,255,255,0.98)", borderBottom: "1px solid #e5e7eb", display: "grid", gridTemplateColumns: "44px 1fr 44px", alignItems: "center", padding: "0 10px", position: "sticky", top: 0, zIndex: 10, backdropFilter: "blur(10px)" };
const backButton = { width: 38, height: 38, borderRadius: "50%", border: 0, background: "#f8fafc", display: "grid", placeItems: "center", color: "#1f2937" };
const title = { margin: 0, justifySelf: "center", fontSize: "clamp(17px, 5vw, 22px)", fontWeight: 900, display: "flex", alignItems: "center", gap: 7 };
const headerSpacer = { width: 48 };
const main = { maxWidth: 760, margin: "0 auto", padding: "14px 14px 18px", display: "grid", gap: 14, boxSizing: "border-box" };
const historyCard = { minHeight: 92, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 24, display: "grid", gridTemplateColumns: "52px 1fr 24px", alignItems: "center", gap: 12, padding: "0 16px", textAlign: "left", boxShadow: "0 10px 26px rgba(15,23,42,.06)" };
const historyIcon = { width: 42, height: 42, borderRadius: 13, background: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center" };
const historyText = { display: "grid", gap: 2, fontSize: 15 };
const historyArrow = { color: "#cbd5e1", display: "grid", placeItems: "center" };
const slotList = { display: "grid", gap: 14 };
const slotMarketCard = { minHeight: 84, border: "2px dashed #dbe3ef", borderRadius: 24, background: "#fff", display: "grid", gridTemplateColumns: "98px 1fr 108px", alignItems: "center", gap: 12, padding: "0 14px", boxShadow: "0 8px 18px rgba(15,23,42,.04)" };
const timePill = { minHeight: 46, borderRadius: 16, background: "#f8fafc", color: "#94a3b8", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 13, border: "1px solid #e2e8f0", padding: "0 10px" };
const timePillActive = { ...timePill, background: "#eaf2ff", color: "#2563eb", border: "1px solid #dbeafe" };
const slotResult = { fontSize: "clamp(24px, 6vw, 34px)", letterSpacing: ".12em", fontWeight: 900, color: "#1f2937", whiteSpace: "nowrap", textAlign: "center" };
const slotResultPending = { ...slotResult, color: "#d6dbe4" };
const slotPlayButton = { height: 48, border: 0, borderRadius: 20, background: "linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 16, fontWeight: 900, boxShadow: "0 12px 22px rgba(37,99,235,0.28)" };
const slotPlayDisabled = { ...slotPlayButton, background: "linear-gradient(180deg,#e5e7eb,#cbd5e1)", color: "#64748b", boxShadow: "none" };

export default StarlineBetPage;
